import React from 'react';
import { TickerIcon } from '../TickerIcon';
import { SignalSparkline } from '../signalSparkline';

interface MarketIndex {
  ticker: string;
  price: number | string;
  change_pct: number | string;
  chart_data?: { time: string; price: number; is_catalyst_event?: boolean }[];
}

interface MarketTickerProps {
  indices?: MarketIndex[];
  isError?: boolean;
}

export function MarketTicker({ indices, isError }: MarketTickerProps) {
  const hasRealData = indices && indices.length > 0;

  if (isError || !hasRealData) {
    return null;
  }

  // ~5s per ticker so longer scanner lists don't fly by
  const durationSec = Math.max(40, indices.length * 5);

  return (
    <div className="w-full bg-gray-50 dark:bg-cat-sidebar border-b border-gray-200 dark:border-cat-border py-2 overflow-hidden select-none flex items-center transition-colors duration-200">
      <div
        className="flex whitespace-nowrap animate-marquee items-center"
        style={{ animationDuration: `${durationSec}s` }}
      >
        {[...indices, ...indices].map((item, index) => {
          const changeStr = String(item.change_pct);
          const changeNum = parseFloat(changeStr.replace('%', '')) || 0;
          const isUp = changeNum >= 0;
          const priceNum = typeof item.price === 'number'
            ? item.price
            : parseFloat(String(item.price).replace(/,/g, '')) || 0;

          const displayChange = typeof item.change_pct === 'number'
            ? `${isUp ? '+' : ''}${item.change_pct.toFixed(2)}%`
            : item.change_pct;

          return (
            <div key={index} className="inline-flex items-center mx-8 font-mono text-xs gap-3">
              <TickerIcon ticker={item.ticker} size="xs" />

              <span className="bg-gray-100 text-gray-700 border-gray-200 dark:bg-cat-elevated dark:text-cat-text-main dark:border-cat-border-2 font-mono text-[11px] font-black px-2.5 py-1 rounded-lg border tracking-wider uppercase transition-colors duration-200">
                {item.ticker}
              </span>

              <span className="text-gray-800 dark:text-[#d5deea] font-bold">
                {item.price}
              </span>

              <span className={`font-semibold ${isUp ? 'text-emerald-600 dark:text-cat-green' : 'text-red-600 dark:text-cat-red'}`}>
                {displayChange}
              </span>

              <div className="w-16 h-4 flex items-center opacity-80 overflow-hidden">
                <div className="origin-left scale-[0.55]">
                  <SignalSparkline
                    chartData={item.chart_data || []}
                    currentPrice={priceNum}
                    changePct={changeNum}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
